/**
 * cart-line-pairing.js — one source of truth for "this design's lines in the cart".
 *
 * Every customizer used to decide whether an add-to-cart was a re-add by
 * consulting a list of cart _OrderIDs captured ONCE by an XHR at page load
 * (moon's `cart_order_id_present`, starmap's `state.cartOrderIds`,
 * locationmap's `state.editMode`, …). After a successful add the customizer
 * redirects to /cart, so any page instance restored from the back-forward
 * cache — or a second tab — still believes the cart is empty. Clicking
 * "Add to cart" there skipped the remove-old-lines branch entirely and pushed
 * a SECOND print plus a SECOND paid gift add-on. The gift lines are hidden
 * from the cart rows (they render inside the print's row instead), so the
 * duplicate £4.99 was invisible and unremovable. Real example:
 * SkylitStudio#191407 — one moon map, two bundles, both charged.
 *
 * purgeDesign() replaces that snapshot with a fresh /cart.js read at the
 * moment of the click, so the re-add path can no longer be skipped.
 */
(function () {
  if (window.SkylitPairing) return;

  var EDIT_FLAG_TIMEOUT_MS = 15000;
  var editFlagTimer = null;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function postCart(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return null; });
    });
  }

  // Copy reference for one physical print in the cart. The design's _OrderID is
  // deliberately shared between copies of the same print, so add-ons pair to
  // this instead. Matches the format cart.js uses when a quantity increase
  // splits a grouped line into independently wrappable copies.
  function makeLineRef() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'copy-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  // cart-attrs-sync skips its orphan reap while this is set: remove-then-add is
  // not atomic, and mid-flight the add-ons look orphaned.
  function holdEditFlag() {
    window.__skylitCartEditInFlight = true;
    clearTimeout(editFlagTimer);
    editFlagTimer = setTimeout(releaseEditFlag, EDIT_FLAG_TIMEOUT_MS);
  }

  function releaseEditFlag() {
    clearTimeout(editFlagTimer);
    window.__skylitCartEditInFlight = false;
  }

  // Every line belonging to a design: the print itself plus anything paired to
  // it (ribbon / gift wrap / bundle / adhesive wall strips, and any future
  // add-on that adopts the same pairing contract).
  function designLineKeys(cart, orderId) {
    return (cart.items || [])
      .filter(function (item) {
        var props = item.properties || {};
        return (props._OrderID || props.OrderID) === orderId ||
               props._paired_with_order_id === orderId;
      })
      .map(function (item) { return item.key; });
  }

  /**
   * Remove every cart line belonging to `orderId`, reading the live cart first.
   * Call this immediately before adding the design — it is a no-op when the
   * design isn't in the cart, so callers no longer need an edit-mode flag.
   * Resolves to { removed: <line count> } and never rejects: a failed purge
   * must not block the add-to-cart the customer asked for.
   *
   * One removal per pass, re-reading the cart each time. This store re-hashes
   * line keys after a mutation, so a chain of removals built from a single
   * snapshot silently no-ops on every key after the first — which is how the
   * old edit paths could leave a stray paid gift line behind even when they
   * did run.
   */
  var MAX_PASSES = 12;
  function purgeDesign(orderId) {
    if (!orderId) return Promise.resolve({ removed: 0 });
    holdEditFlag();
    var removed = 0;

    function pass(n) {
      if (n >= MAX_PASSES) {
        console.warn('[cart-line-pairing] purge hit the pass ceiling for', orderId);
        return Promise.resolve({ removed: removed });
      }
      return fetch(root() + 'cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var keys = designLineKeys(cart, orderId);
          if (!keys.length) return { removed: removed };
          return postCart('cart/change.js', { id: keys[0], quantity: 0 })
            .then(function () {
              removed += 1;
              return pass(n + 1);
            });
        });
    }

    return pass(0)
      .then(function (result) {
        if (result.removed) {
          console.log('[cart-line-pairing] purged', result.removed, 'line(s) for', orderId);
        }
        return result;
      })
      .catch(function (e) {
        console.warn('[cart-line-pairing] purge failed', e);
        return { removed: removed };
      });
  }

  // ── Cart-upsell frame awareness ────────────────────────────────────────────
  // A frame added from the cart's "Complete the look" upsell is a separate
  // line item paired to the print (properties _paired_with_key +
  // _frame_for_order_id — see snippets/cart-frame-upsell.liquid). When a
  // customizer re-opens that design in edit mode it should hydrate as the
  // Framed variant so the customer sees their frame; on confirm it then owns
  // the framing state and consumes the upsell line via purgeFrames().

  var FRAME_HANDLE = 'skylit-studio-frame';
  // Frame product option1 → the print products' "Frame Color" option value.
  var FRAME_COLOUR_BY_OPTION = {
    'Classic Black Frame': 'Black',
    'Elegant White Frame': 'White',
    'Natural Brown Frame': 'Natural',
    'Dark Brown Frame': 'Dark Brown'
  };

  function frameLinesFor(cart, orderId) {
    var items = cart.items || [];
    var printKeys = {};
    var printRefs = {};
    items.forEach(function (it) {
      var p = it.properties || {};
      if ((p._OrderID || p.OrderID) === orderId) {
        printKeys[it.key] = true;
        if (p._cart_line_ref) printRefs[p._cart_line_ref] = true;
      }
    });
    return items.filter(function (it) {
      if (it.handle !== FRAME_HANDLE) return false;
      var p = it.properties || {};
      return p._frame_for_order_id === orderId || printKeys[p._paired_with_key] || printRefs[p._paired_with_line_ref];
    });
  }

  /**
   * The cart-upsell frame paired to `orderId`, if any. Resolves
   * { key, frameOption, colour, size } or null — `colour` is already mapped
   * to the print products' Frame Color value (Black/White/Natural/Dark
   * Brown). Never rejects.
   */
  function pairedFrame(orderId) {
    if (!orderId) return Promise.resolve(null);
    return fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var frame = frameLinesFor(cart, orderId)[0];
        if (!frame) return null;
        var opt = (frame.variant_options && frame.variant_options[0]) || '';
        return {
          key: frame.key,
          frameOption: opt,
          colour: FRAME_COLOUR_BY_OPTION[opt] || 'Black',
          size: (frame.variant_options && frame.variant_options[1]) || ''
        };
      })
      .catch(function () { return null; });
  }

  /**
   * Remove every cart-upsell frame line belonging to `orderId`. Call ONLY
   * from a customizer that hydrated the paired frame into its own selection —
   * the confirm that follows expresses the framing state itself (Framed
   * variant, or deliberately unframed). One removal per pass, fresh cart read
   * each time (line keys re-hash after mutations). Never rejects.
   */
  var MAX_FRAME_PASSES = 6;
  function purgeFrames(orderId) {
    if (!orderId) return Promise.resolve({ removed: 0 });
    holdEditFlag();
    var removed = 0;
    function pass(n) {
      if (n >= MAX_FRAME_PASSES) return Promise.resolve({ removed: removed });
      return fetch(root() + 'cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var frames = frameLinesFor(cart, orderId);
          if (!frames.length) return { removed: removed };
          return postCart('cart/change.js', { id: frames[0].key, quantity: 0 })
            .then(function () {
              removed += 1;
              return pass(n + 1);
            });
        });
    }
    return pass(0).catch(function (e) {
      console.warn('[cart-line-pairing] frame purge failed', e);
      return { removed: removed };
    });
  }

  var FRAME_OPTION_BY_COLOUR = {
    'Black': 'Classic Black Frame',
    'White': 'Elegant White Frame',
    'Natural': 'Natural Brown Frame',
    'Dark Brown': 'Dark Brown Frame'
  };
  // Print variant option2 → the frame product's size option. Mirrors the map
  // in snippets/cart-frame-upsell.liquid.
  var FRAME_SIZE_BY_PRINT_SIZE = {
    'A4': 'A4',
    '30 x 40 cm': '30cm x 40cm',
    '40 x 50 cm': '40cm x 50cm',
    '50 x 70 cm': '50cm x 70cm',
    '60 x 80 cm': '60cm x 80cm'
  };

  var frameVariantsPromise = null;
  function frameVariants() {
    if (!frameVariantsPromise) {
      frameVariantsPromise = fetch(root() + 'products/skylit-studio-frame.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (p) { return (p && p.variants) || []; })
        .catch(function () { frameVariantsPromise = null; return []; });
    }
    return frameVariantsPromise;
  }

  /**
   * Bring the design's frame add-on line in line with what the customizer
   * confirmed, PRESERVING the two-line print + add-on structure:
   *   colour 'Black'|'White'|'Natural'|'Dark Brown' → the add-on exists,
   *     matches that colour, and is sized to the print now in the cart;
   *   colour null → the customer deselected the frame; the add-on goes.
   * Call AFTER the confirm has re-added the print line (the add-on pairs to
   * its key). Never rejects.
   */
  function syncFrameAddon(orderId, colour) {
    if (!orderId) return Promise.resolve();
    if (!colour) return purgeFrames(orderId);
    holdEditFlag();
    return Promise.all([
      fetch(root() + 'cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }),
      frameVariants()
    ]).then(function (res) {
      var cart = res[0];
      var variants = res[1];
      var print = (cart.items || []).filter(function (it) {
        var p = it.properties || {};
        if ((p._OrderID || p.OrderID) !== orderId) return false;
        return ((it.variant_options && it.variant_options[0]) || '') === 'Print';
      })[0];
      // No unframed print landed (framed/canvas variant, or the add failed):
      // the add-on has nothing to pair to.
      if (!print) return purgeFrames(orderId);

      var frameSize = FRAME_SIZE_BY_PRINT_SIZE[(print.variant_options && print.variant_options[1]) || ''];
      var frameOption = FRAME_OPTION_BY_COLOUR[colour];
      var target = null;
      if (frameSize && frameOption) {
        target = variants.filter(function (v) {
          return v.option1 === frameOption && v.option2 === frameSize && v.available !== false;
        })[0];
      }
      if (!target) return purgeFrames(orderId);

      var existing = frameLinesFor(cart, orderId);
      var label = (print.properties && print.properties.Title) || print.product_title || 'print';
      var code = String(print.key).split(':').pop().slice(0, 6).toUpperCase();
      function pairedProperties(previous) {
        var props = Object.assign({}, previous || {}, {
          'Pairs with': code ? label + ' (#' + code + ')' : label,
          _paired_with_key: print.key,
          _frame_for_order_id: orderId
        });
        // Confirm edits replaces the print and assigns a fresh copy reference.
        // Reconnect now: the gallery wall does not run the cart-page repair.
        if (print.properties && print.properties._cart_line_ref) {
          props._paired_with_line_ref = print.properties._cart_line_ref;
        } else {
          delete props._paired_with_line_ref;
        }
        return props;
      }
      if (existing.length === 1 && Number(existing[0].id) === Number(target.id)) {
        return postCart('cart/change.js', {
          id: existing[0].key,
          quantity: existing[0].quantity,
          properties: pairedProperties(existing[0].properties)
        });
      }
      return purgeFrames(orderId).then(function () {
        // Purging can re-hash every key, so read the replacement print again.
        return fetch(root() + 'cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      }).then(function (fresh) {
        var ref = print.properties && print.properties._cart_line_ref;
        print = (fresh.items || []).filter(function (it) {
          var p = it.properties || {};
          return ref ? p._cart_line_ref === ref : (p._OrderID || p.OrderID) === orderId && it.handle !== FRAME_HANDLE;
        })[0];
        if (!print) throw new Error('Edited print is missing from the basket');
        code = String(print.key).split(':').pop().slice(0, 6).toUpperCase();
        return postCart('cart/add.js', { items: [{ id: target.id, quantity: 1, properties: pairedProperties() }] });
      }).then(function () {
        if (!code) return null;
        var attrBody = { attributes: {} };
        attrBody.attributes['_pair_' + code] = label;
        return postCart('cart/update.js', attrBody);
      });
    }).catch(function (e) {
      console.warn('[cart-line-pairing] frame add-on sync failed', e);
    });
  }

  window.SkylitPairing = {
    makeLineRef: makeLineRef,
    purgeDesign: purgeDesign,
    designLineKeys: designLineKeys,
    pairedFrame: pairedFrame,
    purgeFrames: purgeFrames,
    syncFrameAddon: syncFrameAddon,
    holdEditFlag: holdEditFlag,
    releaseEditFlag: releaseEditFlag
  };
})();
